import type {
  FolderData,
  ImageAnalysisResult,
  ImageOrientation,
  ImagePaletteColor,
  ItemTagData,
} from "./types";


const MAX_COLOR_COUNT = 3;
const COLOR_BUCKET_SIZE = 32;
const MIN_ALPHA = 160;
const MIN_COLOR_DISTANCE = 48;
const SAMPLE_SIZE = 48;

function toBucketChannel(value: number) {
  return Math.min(
    255,
    Math.max(0, Math.round(value / COLOR_BUCKET_SIZE) * COLOR_BUCKET_SIZE),
  );
}

function rgbToHex(red: number, green: number, blue: number) {
  return `#${[red, green, blue]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}

function getImageOrientation(width: number, height: number): ImageOrientation {
  if (width === height) {
    return "square";
  }

  return width > height ? "landscape" : "portrait";
}

function getColorDistance(
  source: [number, number, number],
  target: [number, number, number],
) {
  const [sourceRed, sourceGreen, sourceBlue] = source;
  const [targetRed, targetGreen, targetBlue] = target;

  return Math.sqrt(
    (sourceRed - targetRed) ** 2 +
      (sourceGreen - targetGreen) ** 2 +
      (sourceBlue - targetBlue) ** 2,
  );
}

function extractVisualMeta(imageData: ImageData) {
  const buckets = new Map<string, { count: number; rgb: [number, number, number] }>();
  let totalPixels = 0;

  for (let index = 0; index < imageData.data.length; index += 4) {
    const red = imageData.data[index];
    const green = imageData.data[index + 1];
    const blue = imageData.data[index + 2];
    const alpha = imageData.data[index + 3];

    if (alpha < MIN_ALPHA) {
      continue;
    }

    totalPixels += 1;

    const bucketRed = toBucketChannel(red);
    const bucketGreen = toBucketChannel(green);
    const bucketBlue = toBucketChannel(blue);
    const key = `${bucketRed}-${bucketGreen}-${bucketBlue}`;

    const currentBucket = buckets.get(key);
    if (currentBucket) {
      currentBucket.count += 1;
      continue;
    }

    buckets.set(key, {
      count: 1,
      rgb: [bucketRed, bucketGreen, bucketBlue],
    });
  }

  const sortedBuckets = [...buckets.values()].sort((left, right) => right.count - left.count);
  const palettes: ImagePaletteColor[] = [];
  const selectedColors: [number, number, number][] = [];

  for (const bucket of sortedBuckets) {
    const isTooClose = selectedColors.some(
      (selectedColor) =>
        getColorDistance(selectedColor, bucket.rgb) < MIN_COLOR_DISTANCE,
    );

    if (isTooClose) {
      continue;
    }

    selectedColors.push(bucket.rgb);
    palettes.push({
      hex: rgbToHex(...bucket.rgb),
      coverage: Number((bucket.count / totalPixels).toFixed(4)),
    });

    if (palettes.length === MAX_COLOR_COUNT) {
      break;
    }
  }

  return {
    palettes,
  };
}

function loadImageFromFile(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("图片解析失败"));
    };

    image.src = imageUrl;
  });
}

/**
 * 根据 folder_id 递归查找文件夹名称
 */
export function getFolderNameById(
  folderId: string,
  folders: FolderData[]
): string {
  const findFolder = (id: string, folderList: FolderData[]): FolderData | null => {
    for (const folder of folderList) {
      if (folder.id === id) {
        return folder;
      }
      if (folder.sub_folders && folder.sub_folders.length > 0) {
        const found = findFolder(id, folder.sub_folders);
        if (found) return found;
      }
    }
    return null;
  };

  const folder = findFolder(folderId, folders);
  return folder?.name || '未分类';
}

interface FolderTreeNode {
  id: string;
  name: string;
  parent_id?: string;
  children?: FolderTreeNode[];
}

/**
 * 根据文件夹ID获取父文件夹ID
 */
export function getFolderParentIdById(
  folderId: string,
  folders: FolderTreeNode[]
): string | null {
  const findParent = (
    targetId: string,
    folderList: FolderTreeNode[],
    parentId: string | null
  ): string | null | undefined => {
    for (const folder of folderList) {
      if (folder.id === targetId) {
        return parentId;
      }
      if (folder.children && folder.children.length > 0) {
        const result = findParent(targetId, folder.children, String(folder.id));
        if (result !== null) {
          return result;
        }
      }
    }
    return undefined;
  };

  return findParent(folderId, folders, null) ?? null;
}

/**
 * 从 URL 中提取网站名称
 */
export const getSourceName = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, "");
    const parts = hostname.split(".");
    const mainDomain = parts.length > 1 ? parts[parts.length - 2] : parts[0];
    return mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1).toLowerCase();
  } catch {
    return "Website";
  }
};

export function getItemTagName(tag: ItemTagData | string) {
  return typeof tag === "string" ? tag : tag.name;
}

export function getItemTagId(tag: ItemTagData | string) {
  return typeof tag === "string" ? tag : tag.id;
}

export function getItemTagNames(tags?: Array<ItemTagData | string>) {
  return Array.isArray(tags) ? tags.map(getItemTagName) : [];
}

export function getItemTagIds(tags?: Array<ItemTagData | string>) {
  return Array.isArray(tags) ? tags.map(getItemTagId) : [];
}

export async function analyzeImageFile(file: File): Promise<ImageAnalysisResult> {
  const image = await loadImageFromFile(file);
  const width = image.naturalWidth;
  const height = image.naturalHeight;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });

  if (!context) {
    throw new Error("浏览器不支持图片分析");
  }

  const ratio = Math.min(1, SAMPLE_SIZE / Math.max(width, height));
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const visual = extractVisualMeta(imageData);

  return {
    aspect_ratio: Number((width / height).toFixed(4)),
    extension,
    file_name: file.name,
    height,
    mime_type: file.type || "application/octet-stream",
    orientation: getImageOrientation(width, height),
    size: file.size,
    visual,
    width,
  };
}
